import styled from '@emotion/styled';

// Icon-only button that reveals its label as a floating tooltip on hover — used where several
// actions need to sit side by side without each one's text label competing for space. Pass a
// distinct `$color` per button so adjacent actions stay visually distinguishable even before the
// label appears.
//
// The label is `position: absolute` rather than an inline reveal that widens the button: an
// inline reveal changes the button's box size, and inside a `flex-wrap` toolbar that can flip the
// row's wrap decision the instant a label expands — which un-hovers the button, collapses the
// label, un-wraps, re-hovers, expands again, forever (until the mouse moves away). Keeping the
// label out of layout flow entirely avoids that feedback loop regardless of how tight the toolbar
// is. `&.show` mirrors `:hover` so a consumer that also renders this as a dropdown toggle (see
// `DropdownToggle`'s `tag` prop, which already applies a `show` class while `isOpen`) can keep the
// label visible for as long as its menu is open, not just while the pointer is over it.
export const HoverLabelButton = styled('button', {
  shouldForwardProp: prop => prop !== '$color',
})<{ $color: string }>`
  position: relative;
  background: transparent;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 4px;
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  transition: background-color 0.15s ease;

  .hover-label {
    position: absolute;
    top: 50%;
    right: 100%;
    transform: translateY(-50%) translateX(-4px);
    background: ${p => p.$color};
    color: #fff;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.4;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.15s ease;
    z-index: 30;
  }

  &:hover:not(:disabled),
  &.show {
    background-color: ${p => p.$color}22;
  }

  &:hover:not(:disabled) .hover-label,
  &.show .hover-label {
    opacity: 1;
    visibility: visible;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export default HoverLabelButton;
